import React, { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  AvatarBadge,
  Box,
  Button,
  Center,
  DrawerCloseButton,
  Flex,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  Textarea,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  MdArrowBack,
  MdAttachFile,
  MdClose,
  MdDelete,
  MdDone,
  MdDoneAll,
  MdGroupAdd,
  MdGroups,
  MdInsertDriveFile,
  MdLogout,
  MdMic,
  MdMoreVert,
  MdSend,
} from "react-icons/md";
import Swal from "sweetalert2";
import { estiloAlerta } from "utils/estiloAlertas";
import { etiquetaDia, horaCorta, mismoDia } from "./formato";
import { enumerar } from "./texto";
import Adjunto from "./Adjunto";
import { MAXIMO_ARCHIVOS_POR_ENVIO, formatoDuracion, formatoTamano, problemaDelArchivo } from "./archivos";
import useGrabadora from "./useGrabadora";

export const LARGO_MAXIMO_MENSAJE = 4000;
// A esta distancia del final se considera que la persona está leyendo lo último.
const CERCA_DEL_FINAL_PX = 120;

const URL_EN_TEXTO = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"])/gi;

/** El texto tal cual, con los enlaces http(s) que traiga como enlaces. */
function TextoConEnlaces({ texto }) {
  const partes = texto.split(URL_EN_TEXTO);
  return partes.map((parte, indice) => (indice % 2 === 1
    ? <Link key={indice} href={parte} isExternal textDecoration="underline" wordBreak="break-all">{parte}</Link>
    : <Fragment key={indice}>{parte}</Fragment>));
}

const confirmarSalida = async (nombre) => {
  const resultado = await Swal.fire({
    ...estiloAlerta(),
    title: `¿Salir de «${nombre}»?`,
    text: "Dejarás de ver sus mensajes. Alguien del grupo puede volver a agregarte.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    confirmButtonText: "Sí, salir",
    cancelButtonText: "Cancelar",
    // Igual que confirmDelete: por encima del panel y sin que este le quite el foco.
    willOpen: () => {
      const contenedor = Swal.getContainer();
      if (contenedor) {
        contenedor.style.zIndex = "1500";
        contenedor.setAttribute("data-no-focus-lock", "true");
      }
    },
  });
  return resultado.isConfirmed;
};

function Burbuja({ mensaje, propio, mostrarAutor, leido, directa, onReintentar, onDescartar }) {
  const fondoAjeno = useColorModeValue("gray.100", "navy.700");
  const colorAjeno = useColorModeValue("gray.800", "white");
  const colorHora = useColorModeValue("gray.500", "gray.400");
  const colorAutor = useColorModeValue("brand.600", "brand.200");
  // Foto o video: casi sin borde, como en cualquier chat.
  const conMedio = ["imagen", "video"].includes(mensaje.adjunto?.categoria) && !mensaje.adjunto.vencido;

  return (
    <Flex direction="column" align={propio ? "flex-end" : "flex-start"} mt={mostrarAutor ? "10px" : "3px"}>
      {mostrarAutor && (
        <Text fontSize="xs" fontWeight="700" color={colorAutor} mb="2px" px="4px">{mensaje.autor}</Text>
      )}
      <Box
        maxW="82%"
        px={conMedio ? "4px" : "12px"}
        py={conMedio ? "4px" : "7px"}
        borderRadius="16px"
        borderBottomRightRadius={propio ? "4px" : "16px"}
        borderBottomLeftRadius={propio ? "16px" : "4px"}
        bg={propio ? "brand.500" : fondoAjeno}
        color={propio ? "white" : colorAjeno}
        opacity={mensaje.estado === "enviando" || mensaje.estado === "subiendo" ? 0.8 : 1}
        fontSize="sm"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
        data-testid="burbuja"
      >
        {mensaje.adjunto && <Adjunto mensaje={mensaje} propio={propio} />}
        {mensaje.texto && (
          <Box px={conMedio ? "8px" : 0} pb={conMedio ? "4px" : 0}>
            <TextoConEnlaces texto={mensaje.texto} />
          </Box>
        )}
      </Box>
      <Flex align="center" gap="4px" mt="2px" px="4px" fontSize="11px" color={colorHora}>
        {mensaje.estado === "error" ? (
          <>
            <Text color="red.400">{mensaje.error || "No se envió"}</Text>
            <Button size="xs" variant="link" colorScheme="red" onClick={() => onReintentar(mensaje)}>Reintentar</Button>
            <Button size="xs" variant="link" onClick={() => onDescartar(mensaje.id)}>Descartar</Button>
          </>
        ) : (
          <>
            <Text>{mensaje.estado === "enviando" ? "Enviando…" : mensaje.estado === "subiendo" ? "" : horaCorta(mensaje.fecha)}</Text>
            {propio && directa && !mensaje.local && (
              <Icon
                as={leido ? MdDoneAll : MdDone}
                boxSize="14px"
                color={leido ? "blue.400" : colorHora}
                aria-label={leido ? "Visto" : "Enviado"}
              />
            )}
          </>
        )}
      </Flex>
    </Flex>
  );
}

function ArchivoPorEnviar({ pendiente, onQuitar }) {
  const fondo = useColorModeValue("white", "whiteAlpha.100");
  const bordes = useColorModeValue("gray.200", "whiteAlpha.300");
  return (
    <Flex align="center" gap="6px" bg={fondo} border="1px solid" borderColor={bordes} borderRadius="10px" p="4px" pe="2px" maxW="190px">
      {pendiente.vistaPrevia ? (
        <Box as="img" src={pendiente.vistaPrevia} alt="" w="32px" h="32px" objectFit="cover" borderRadius="6px" flexShrink={0} />
      ) : (
        <Icon as={MdInsertDriveFile} boxSize="24px" color="gray.400" flexShrink={0} />
      )}
      <Box minW={0}>
        <Text fontSize="xs" fontWeight="600" noOfLines={1} title={pendiente.archivo.name}>{pendiente.archivo.name}</Text>
        <Text fontSize="10px" color="gray.500">{formatoTamano(pendiente.archivo.size)}</Text>
      </Box>
      <IconButton aria-label={`Quitar ${pendiente.archivo.name}`} icon={<MdClose />} size="xs" variant="ghost" onClick={() => onQuitar(pendiente.id)} />
    </Flex>
  );
}

const soltarVistasPrevias = (pendientes) => pendientes.forEach((pendiente) => {
  if (pendiente.vistaPrevia && typeof URL.revokeObjectURL === "function") URL.revokeObjectURL(pendiente.vistaPrevia);
});

export default function Conversacion({ chat, limites = {}, onAgregarPersonas }) {
  const { hilo, usuarioId } = chat;
  const [borrador, setBorrador] = useState("");
  // Archivos elegidos, pegados o soltados que salen al enviar.
  const [pendientes, setPendientes] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [arrastrando, setArrastrando] = useState(false);
  const contenedorRef = useRef(null);
  const campoRef = useRef(null);
  const selectorRef = useRef(null);
  const grabadora = useGrabadora({ alLlegarAlMaximo: (nota) => chat.enviarArchivos([nota]) });
  const cercaDelFinalRef = useRef(true);
  const alturaAntesRef = useRef(null);

  const bordes = useColorModeValue("gray.200", "whiteAlpha.200");
  const textoSecundario = useColorModeValue("gray.500", "gray.400");
  const fondoSistema = useColorModeValue("gray.50", "whiteAlpha.100");
  const fondoEntrada = useColorModeValue("gray.50", "navy.900");
  const colorTitulo = useColorModeValue("secondaryGray.900", "white");
  const fondoArrastre = useColorModeValue("whiteAlpha.900", "blackAlpha.700");

  const conversacion = chat.conversaciones.find((item) => item.id === hilo.conversacionId) || null;
  const directa = conversacion ? conversacion.tipo === "directa" : true;
  const titulo = conversacion?.nombre || hilo.destinatario?.nombre || "Conversación";
  const contactoId = conversacion?.contactoId ?? hilo.destinatario?.id ?? null;
  const participantes = useMemo(
    () => (hilo.participantes.length ? hilo.participantes : conversacion?.participantes || []),
    [hilo.participantes, conversacion?.participantes]
  );
  const contacto = participantes.find((participante) => participante.id === contactoId);
  const contactoInactivo = directa && contacto && contacto.activo === false;
  const otroUltimoLeido = directa ? contacto?.ultimoLeido || 0 : 0;
  const quienEscribe = hilo.conversacionId ? chat.escribiendo[hilo.conversacionId] : null;
  const conectado = chat.estado === "conectado";

  const subtitulo = useMemo(() => {
    if (quienEscribe) return directa ? "Escribiendo…" : `${quienEscribe.nombre.split(" ")[0]} está escribiendo…`;
    if (directa) {
      if (contactoInactivo) return "Usuario inactivo";
      return chat.enLinea.has(contactoId) ? "En línea" : "Desconectado";
    }
    const nombres = participantes.filter((p) => p.id !== usuarioId).map((p) => p.nombre.split(" ")[0]);
    return nombres.length > 4 ? `${nombres.slice(0, 4).join(", ")} y ${nombres.length - 4} más` : enumerar(["Tú", ...nombres]);
  }, [quienEscribe, directa, contactoInactivo, chat.enLinea, contactoId, participantes, usuarioId]);

  const alDesplazar = () => {
    const nodo = contenedorRef.current;
    if (!nodo) return;
    cercaDelFinalRef.current = nodo.scrollHeight - nodo.scrollTop - nodo.clientHeight < CERCA_DEL_FINAL_PX;
  };

  // Al llegar algo nuevo se baja solo si ya estaba abajo (o lo escribió uno mismo); al
  // cargar mensajes anteriores se conserva lo que estaba viendo.
  const ultimo = hilo.mensajes[hilo.mensajes.length - 1];
  const primeroId = hilo.mensajes[0]?.id;
  useLayoutEffect(() => {
    const nodo = contenedorRef.current;
    if (!nodo) return;
    if (alturaAntesRef.current !== null) {
      nodo.scrollTop += nodo.scrollHeight - alturaAntesRef.current;
      alturaAntesRef.current = null;
      return;
    }
    if (cercaDelFinalRef.current || ultimo?.usuarioId === usuarioId) {
      nodo.scrollTop = nodo.scrollHeight;
    }
  }, [ultimo?.id, ultimo?.usuarioId, primeroId, hilo.cargando, usuarioId]);

  // Otra conversación (al tocar un aviso no se pasa por la lista). Una directa nueva sigue
  // siendo la misma cuando el primer mensaje le da su id: lo que se escribe no se borra.
  const claveHilo = hilo.destinatario ? `persona:${hilo.destinatario.id}` : `conversacion:${hilo.conversacionId}`;
  const { cancelar: cancelarNotaDeVoz } = grabadora;
  useEffect(() => {
    cercaDelFinalRef.current = true;
    setBorrador("");
    setAvisos([]);
    setPendientes((previos) => {
      soltarVistasPrevias(previos);
      return [];
    });
    // Una nota de voz a medias no se envía a la conversación que se acaba de abrir.
    cancelarNotaDeVoz();
    campoRef.current?.focus();
  }, [claveHilo, cancelarNotaDeVoz]);

  const agregarArchivos = (lista) => {
    const archivos = Array.from(lista || []);
    if (!archivos.length) return;
    const problemas = [];
    const aceptados = archivos.filter((archivo) => {
      const problema = problemaDelArchivo(archivo, limites);
      if (problema) problemas.push(problema);
      return !problema;
    });
    const espacio = Math.max(0, MAXIMO_ARCHIVOS_POR_ENVIO - pendientes.length);
    if (aceptados.length > espacio) problemas.push(`Puedes enviar hasta ${MAXIMO_ARCHIVOS_POR_ENVIO} archivos a la vez.`);
    setAvisos(problemas);
    setPendientes((previos) => [
      ...previos,
      ...aceptados.slice(0, espacio).map((archivo, indice) => ({
        id: `${Date.now()}-${indice}-${archivo.name}`,
        archivo,
        vistaPrevia: archivo.type.startsWith("image/") && typeof URL.createObjectURL === "function" ? URL.createObjectURL(archivo) : null,
      })),
    ]);
    campoRef.current?.focus();
  };

  const quitarPendiente = (id) => {
    setPendientes((previos) => {
      soltarVistasPrevias(previos.filter((pendiente) => pendiente.id === id));
      return previos.filter((pendiente) => pendiente.id !== id);
    });
  };

  const enviarNotaDeVoz = async () => {
    const nota = await grabadora.terminar();
    if (nota) chat.enviarArchivos([nota]);
  };

  // Soltar archivos sobre la conversación.
  const conArchivos = (evento) => Array.from(evento.dataTransfer?.types || []).includes("Files");
  const alArrastrarEncima = (evento) => {
    if (!conArchivos(evento)) return;
    evento.preventDefault();
    setArrastrando(true);
  };
  const alSalirDelArrastre = (evento) => {
    if (!evento.currentTarget.contains(evento.relatedTarget)) setArrastrando(false);
  };
  const alSoltar = (evento) => {
    if (!conArchivos(evento)) return;
    evento.preventDefault();
    setArrastrando(false);
    agregarArchivos(evento.dataTransfer.files);
  };

  const cargarAnteriores = () => {
    alturaAntesRef.current = contenedorRef.current?.scrollHeight ?? null;
    chat.cargarAnteriores();
  };

  const ajustarAltura = () => {
    const campo = campoRef.current;
    if (!campo) return;
    campo.style.height = "auto";
    campo.style.height = `${Math.min(campo.scrollHeight, 140)}px`;
  };

  const hayQueEnviar = Boolean(borrador.trim() || pendientes.length);

  const enviar = () => {
    if (!hayQueEnviar || !conectado || contactoInactivo) return;
    if (pendientes.length) {
      chat.enviarArchivos(pendientes.map((pendiente) => pendiente.archivo), borrador);
      soltarVistasPrevias(pendientes);
      setPendientes([]);
    } else {
      chat.enviar(borrador);
    }
    setBorrador("");
    setAvisos([]);
    requestAnimationFrame(ajustarAltura);
  };

  const alTeclear = (evento) => {
    // Enter envía; Shift+Enter hace un salto de línea. Mientras se compone un acento no.
    if (evento.key === "Enter" && !evento.shiftKey && !evento.nativeEvent.isComposing) {
      evento.preventDefault();
      enviar();
    }
  };

  const salir = async () => {
    if (!(await confirmarSalida(titulo))) return;
    const error = await chat.salirDeGrupo();
    if (error) Swal.fire({ ...estiloAlerta(), icon: "error", title: "No se pudo salir del grupo", text: error });
  };

  const avisoEntrada = !conectado
    ? (chat.estado === "cerrado" ? "Tu sesión expiró. Vuelve a iniciar sesión para usar el chat." : "Sin conexión. Reconectando…")
    : contactoInactivo ? "Este usuario está inactivo: no puede leer mensajes nuevos." : null;

  return (
    <Flex
      direction="column"
      h="100%"
      minH={0}
      position="relative"
      onDragEnter={alArrastrarEncima}
      onDragOver={alArrastrarEncima}
      onDragLeave={alSalirDelArrastre}
      onDrop={alSoltar}
    >
      {arrastrando && (
        <Center position="absolute" inset="0" zIndex={2} bg={fondoArrastre} border="2px dashed" borderColor="brand.500" borderRadius="12px" m="8px" pointerEvents="none">
          <Text fontWeight="700" color="brand.500">Suelta los archivos para enviarlos</Text>
        </Center>
      )}
      <Flex align="center" gap="10px" px="12px" py="10px" borderBottom="1px solid" borderColor={bordes} pe="48px">
        <IconButton aria-label="Volver a las conversaciones" icon={<MdArrowBack />} variant="ghost" size="sm" onClick={chat.cerrarHilo} />
        {directa ? (
          <Avatar size="sm" name={titulo}>
            {chat.enLinea.has(contactoId) && <AvatarBadge boxSize="1em" bg="green.400" />}
          </Avatar>
        ) : (
          <Avatar size="sm" bg="brand.500" icon={<Icon as={MdGroups} boxSize="20px" color="white" />} />
        )}
        <Box flex="1" minW={0}>
          <Text fontWeight="700" fontSize="md" color={colorTitulo} noOfLines={1}>{titulo}</Text>
          <Text fontSize="xs" color={quienEscribe ? "brand.500" : textoSecundario} noOfLines={1}>{subtitulo}</Text>
        </Box>
        {!directa && (
          <Menu placement="bottom-end" isLazy>
            <MenuButton as={IconButton} aria-label="Opciones del grupo" icon={<MdMoreVert />} variant="ghost" size="sm" />
            <MenuList maxH="60vh" overflowY="auto" zIndex={1500}>
              <MenuItem icon={<MdGroupAdd />} onClick={onAgregarPersonas}>Agregar personas</MenuItem>
              <MenuItem icon={<MdLogout />} color="red.400" onClick={salir}>Salir del grupo</MenuItem>
              <MenuDivider />
              <MenuGroup title={`Miembros (${participantes.length})`}>
                {participantes.map((participante) => (
                  <MenuItem key={participante.id} isDisabled _disabled={{ opacity: 1, cursor: "default" }}>
                    <Flex align="center" gap="8px">
                      <Box w="8px" h="8px" borderRadius="full" bg={chat.enLinea.has(participante.id) ? "green.400" : "gray.300"} />
                      <Text fontSize="sm">{participante.id === usuarioId ? `${participante.nombre} (tú)` : participante.nombre}</Text>
                    </Flex>
                  </MenuItem>
                ))}
              </MenuGroup>
            </MenuList>
          </Menu>
        )}
        <DrawerCloseButton top="14px" />
      </Flex>

      <Box ref={contenedorRef} flex="1" minH={0} overflowY="auto" px="14px" py="10px" onScroll={alDesplazar} data-testid="mensajes">
        {hilo.cargando ? (
          <Center h="100%"><Spinner color="brand.500" /></Center>
        ) : hilo.error ? (
          <Center h="100%"><Text fontSize="sm" color="red.400">{hilo.error}</Text></Center>
        ) : (
          <>
            {hilo.hayMas && (
              <Center mb="8px">
                <Button size="xs" variant="ghost" isLoading={hilo.cargandoAnteriores} onClick={cargarAnteriores}>
                  Cargar mensajes anteriores
                </Button>
              </Center>
            )}
            {!hilo.mensajes.length && (
              <Center h="100%" flexDirection="column" color={textoSecundario} textAlign="center" px="20px">
                <Text fontSize="sm" fontWeight="600">Aún no hay mensajes</Text>
                <Text fontSize="xs" mt="4px">Escribe el primero{directa ? ` a ${titulo.split(" ")[0]}` : ""}.</Text>
              </Center>
            )}
            {hilo.mensajes.map((mensaje, indice) => {
              const anterior = hilo.mensajes[indice - 1];
              const nuevoDia = !anterior || !mismoDia(anterior.fecha, mensaje.fecha);
              const propio = mensaje.usuarioId === usuarioId;
              return (
                <Fragment key={mensaje.id}>
                  {nuevoDia && (
                    <Center my="10px">
                      <Text fontSize="11px" fontWeight="600" color={textoSecundario} bg={fondoSistema} px="10px" py="2px" borderRadius="full">
                        {etiquetaDia(mensaje.fecha)}
                      </Text>
                    </Center>
                  )}
                  {mensaje.tipo === "sistema" ? (
                    <Center my="6px">
                      <Text fontSize="xs" color={textoSecundario} textAlign="center">{mensaje.texto}</Text>
                    </Center>
                  ) : (
                    <Burbuja
                      mensaje={mensaje}
                      propio={propio}
                      directa={directa}
                      leido={!mensaje.local && otroUltimoLeido >= mensaje.id}
                      mostrarAutor={!directa && !propio && (nuevoDia || anterior?.usuarioId !== mensaje.usuarioId || anterior?.tipo === "sistema")}
                      onReintentar={chat.reintentar}
                      onDescartar={chat.descartar}
                    />
                  )}
                </Fragment>
              );
            })}
          </>
        )}
      </Box>

      <Box borderTop="1px solid" borderColor={bordes} px="12px" py="10px">
        {avisoEntrada && <Text fontSize="xs" color={textoSecundario} mb="6px">{avisoEntrada}</Text>}
        {[...avisos, grabadora.error].filter(Boolean).map((aviso) => (
          <Text key={aviso} fontSize="xs" color="red.400" mb="4px">{aviso}</Text>
        ))}
        {pendientes.length > 0 && (
          <Flex gap="6px" wrap="wrap" mb="8px" data-testid="por-enviar">
            {pendientes.map((pendiente) => (
              <ArchivoPorEnviar key={pendiente.id} pendiente={pendiente} onQuitar={quitarPendiente} />
            ))}
          </Flex>
        )}
        <input
          ref={selectorRef}
          type="file"
          multiple
          hidden
          accept={(limites.extensiones || []).map((extension) => `.${extension}`).join(",")}
          onChange={(evento) => {
            agregarArchivos(evento.target.files);
            // Así se puede volver a elegir el mismo archivo.
            evento.target.value = "";
          }}
          data-testid="elegir-archivos"
        />
        {grabadora.grabando ? (
          <Flex align="center" gap="10px" minH="40px">
            <Box w="10px" h="10px" borderRadius="full" bg="red.400" flexShrink={0} />
            <Text fontSize="sm" flex="1">Grabando nota de voz · {formatoDuracion(grabadora.segundos)}</Text>
            <IconButton aria-label="Cancelar la nota de voz" icon={<MdDelete />} variant="ghost" borderRadius="full" onClick={grabadora.cancelar} />
            <IconButton aria-label="Enviar la nota de voz" icon={<MdSend />} colorScheme="brand" borderRadius="full" onClick={enviarNotaDeVoz} isDisabled={!conectado} />
          </Flex>
        ) : (
          <Flex align="flex-end" gap="8px">
            <IconButton
              aria-label="Adjuntar archivos"
              icon={<MdAttachFile />}
              variant="ghost"
              borderRadius="full"
              onClick={() => selectorRef.current?.click()}
              isDisabled={Boolean(contactoInactivo)}
            />
            <Textarea
              ref={campoRef}
              value={borrador}
              onChange={(evento) => {
                setBorrador(evento.target.value);
                ajustarAltura();
                if (evento.target.value.trim()) chat.avisarEscribiendo();
              }}
              onKeyDown={alTeclear}
              onPaste={(evento) => {
                // Una captura de pantalla pegada se adjunta; el texto pegado, no.
                const archivos = Array.from(evento.clipboardData?.files || []);
                if (!archivos.length) return;
                evento.preventDefault();
                agregarArchivos(archivos);
              }}
              placeholder={pendientes.length ? "Agrega un comentario (opcional)" : "Escribe un mensaje"}
              aria-label="Mensaje"
              rows={1}
              resize="none"
              minH="40px"
              maxLength={LARGO_MAXIMO_MENSAJE}
              bg={fondoEntrada}
              borderRadius="20px"
              fontSize="sm"
              py="9px"
              isDisabled={Boolean(contactoInactivo)}
            />
            {!hayQueEnviar && grabadora.disponible ? (
              <IconButton
                aria-label="Grabar nota de voz"
                icon={<MdMic />}
                colorScheme="brand"
                borderRadius="full"
                onClick={grabadora.empezar}
                isDisabled={!conectado || Boolean(contactoInactivo)}
              />
            ) : (
              <IconButton
                aria-label="Enviar"
                icon={<MdSend />}
                colorScheme="brand"
                borderRadius="full"
                onClick={enviar}
                isDisabled={!hayQueEnviar || !conectado || Boolean(contactoInactivo)}
              />
            )}
          </Flex>
        )}
        {borrador.length > LARGO_MAXIMO_MENSAJE - 200 && (
          <Text fontSize="11px" color={textoSecundario} mt="4px" textAlign="right">
            {borrador.length}/{LARGO_MAXIMO_MENSAJE}
          </Text>
        )}
      </Box>
    </Flex>
  );
}
